import React, { useState, useEffect } from 'react';
import { getAllJobs, deleteJob, getJobStatus } from '../../../services/agentcore.service';
import { useNavigate } from 'react-router-dom';
import { agentCoreClient } from '../../../config/http.client';
import { showSuccess, showError, showWarning } from '../../../services/notification.service';
import ConfirmDialog from '../../shared/ConfirmDialog';
import './JobsList.css';

function JobsList() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', job: null, jobId: null });
  const navigate = useNavigate();

  const loadJobs = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Show only report jobs (idea_analysis) in Jobs tab
      const response = await getAllJobs('idea_analysis');
      if (response.success) {
        // Sort jobs by created_at (newest first)
        const sortedJobs = response.jobs.sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setJobs(sortedJobs);
        setError(null);
      } else {
        setError('Failed to load jobs');
      }
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadJobs();
    
    // Poll every 10 seconds - check for active jobs inside interval
    const intervalId = setInterval(() => {
      // Access current jobs via closure - will have latest state
      setJobs(currentJobs => {
        const hasActiveJobs = currentJobs.some(job => 
          job.status === 'processing' || job.status === 'queued'
        );
        
        if (hasActiveJobs) {
          loadJobs(true);
        }
        
        return currentJobs; // Return unchanged
      });
    }, 10000);
    
    return () => clearInterval(intervalId);
  }, []); // Run once on mount

  const handleJobClick = async (job) => {
    if (job.status === 'completed') {
      // Fetch full job details to get the report
      try {
        const jobDetails = await getJobStatus(job.job_id);
        
        // Check if we have report data in the job result
        if (jobDetails.result && jobDetails.result.report) {
          // Store report data in sessionStorage with a special flag
          const reportId = job._id || job.job_id;
          sessionStorage.setItem(`jobReport_${reportId}`, JSON.stringify({
            report: jobDetails.result.report,
            jobId: job.job_id,
            needsApproval: true // Job reports should show save button
          }));
          navigate(`/tools/idea-analyzer/reports/${reportId}`);
        } else if (jobDetails.report_id) {
          // If report already saved to DB, navigate directly
          navigate(`/tools/idea-analyzer/reports/${jobDetails.report_id}`);
        } else {
          showError('Report data not found for this job');
        }
      } catch (error) {
        console.error('Failed to fetch job details:', error);
        showError('Failed to load report details');
      }
    } else if (job.status === 'failed') {
      // Backend sets proper error messages including timeout info
      const errorMsg = `Job failed: ${job.error || 'Unknown error'}`;
      showError(errorMsg);
    } else {
      // Show job details for in-progress jobs
      showWarning(`Job is ${job.status}. Progress: ${job.progress || 0}%`);
    }
  };

  const handleDeleteJob = async (jobId, event) => {
    event.stopPropagation(); // Prevent row click
    setConfirmDialog({ 
      isOpen: true, 
      type: 'delete', 
      jobId: jobId,
      job: null 
    });
  };

  const confirmDeleteJob = async () => {
    const jobId = confirmDialog.jobId;
    setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null });

    try {
      const response = await deleteJob(jobId);
      if (response.success) {
        // Remove from local state
        setJobs(jobs.filter(job => job.job_id !== jobId));
        showSuccess('Job deleted successfully');
      } else {
        showError('Failed to delete job');
      }
    } catch (error) {
      console.error('Failed to delete job:', error);
      showError(`Failed to delete job: ${error.message}`);
    }
  };

  const copyJobId = (jobId, event) => {
    event.stopPropagation();
    
    const button = event.currentTarget;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(jobId)
        .then(() => {
          // Show temporary feedback
          if (button) {
            const originalHTML = button.innerHTML;
            button.innerHTML = '✓';
            button.style.color = '#10b981';
            setTimeout(() => {
              button.innerHTML = originalHTML;
              button.style.color = '';
            }, 1500);
          }
          showSuccess('Job ID copied to clipboard');
        })
        .catch(err => {
          console.error('Failed to copy:', err);
          showError('Failed to copy Job ID: ' + err.message);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = jobId;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        if (button) {
          const originalHTML = button.innerHTML;
          button.innerHTML = '✓';
          button.style.color = '#10b981';
          setTimeout(() => {
            button.innerHTML = originalHTML;
            button.style.color = '';
          }, 1500);
        }
        showSuccess('Job ID copied to clipboard');
      } catch (err) {
        console.error('Fallback copy failed:', err);
        showError('Failed to copy Job ID');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleRetryJob = async (job, event) => {
    event.stopPropagation(); // Prevent row click
    setConfirmDialog({ 
      isOpen: true, 
      type: 'retry', 
      job: job,
      jobId: null 
    });
  };

  const confirmRetryJob = async () => {
    const job = confirmDialog.job;
    setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null });

    try {
      const payload = job.request_payload;
      
      if (!payload || (!payload.prompt && !payload.idea)) {
        showError('Cannot retry: Job data is incomplete');
        return;
      }

      // Extract parameters from the original job
      const backend = payload.backend || 'strands';
      const idea = payload.prompt || payload.idea;  // Support both field names
      const advancedMode = payload.advanced_mode || false;
      const workflowType = payload.workflow_type || 'sequential';
      const modelId = payload.model_id || null;
      const questionnaireData = payload.questionnaire_data || null;

      // Call backend with retry flag and same job_id using agentCoreClient
      const response = await agentCoreClient.post('/agentcore/invoke', {
        action: 'generate-report',
        idea: idea,  // Use 'idea' field name consistently
        prompt: idea,
        backend: backend,
        advanced_mode: advancedMode,
        workflow_type: workflowType,
        use_cache: true,
        model_id: modelId,
        questionnaire_data: questionnaireData,
        retry_job_id: job.job_id  // Pass original job_id for retry
      });

      const result = response.data;

      if (result.status === 'accepted') {
        const retriedTime = new Date().toISOString();
        
        // Immediately update the job status in local state for instant feedback
        setJobs(currentJobs => currentJobs.map(j => 
          j.job_id === job.job_id 
            ? { ...j, status: 'queued', progress: 0, error: null, retried_at: retriedTime, updated_at: retriedTime }
            : j
        ));
        
        showSuccess(`Job retry started successfully! Job ID: ${result.job_id}`);
        
        // Refresh the jobs list to get confirmed status from backend
        setTimeout(() => loadJobs(true), 1000);
      } else {
        showError(`Failed to retry job: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to retry job:', error);
      showError(`Failed to retry job: ${error.message}`);
    }
  };

  const handleRefresh = () => {
    loadJobs(true);
  };

  const getStatusIcon = (status, error) => {
    // Backend marks stale jobs as failed with specific error messages
    if (status === 'failed' && error) {
      if (error.includes('timed out') || error.includes('processing')) {
        return '⏱️'; // Processing timeout
      }
      if (error.includes('stuck in queue') || error.includes('queue')) {
        return '⚠️'; // Stuck in queue
      }
    }
    
    switch (status) {
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      case 'processing':
        return '⚙️';
      case 'queued':
        return '⏳';
      default:
        return '❓';
    }
  };

  const getStatusClass = (status, error) => {
    // Backend marks stale jobs as failed with specific error messages
    if (status === 'failed' && error) {
      if (error.includes('timed out') || error.includes('processing')) {
        return 'status-timeout';
      }
      if (error.includes('stuck in queue') || error.includes('queue')) {
        return 'status-stuck';
      }
    }
    
    switch (status) {
      case 'completed':
        return 'status-completed';
      case 'failed':
        return 'status-failed';
      case 'processing':
        return 'status-processing';
      case 'queued':
        return 'status-queued';
      default:
        return 'status-unknown';
    }
  };

  const getStatusText = (status, error) => {
    // Backend marks stale jobs as failed with specific error messages
    if (status === 'failed' && error) {
      if (error.includes('timed out') || error.includes('processing')) {
        return 'timed out';
      }
      if (error.includes('stuck in queue') || error.includes('queue')) {
        return 'stuck in queue';
      }
    }
    
    return status;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Ensure timestamp is treated as UTC if it doesn't have timezone info
    let dateStr = timestamp;
    if (!timestamp.endsWith('Z') && !timestamp.includes('+') && !timestamp.includes('T')) {
      // If it's just a date string without timezone, append Z for UTC
      dateStr = timestamp + 'Z';
    } else if (timestamp.includes('T') && !timestamp.endsWith('Z') && !timestamp.includes('+')) {
      // ISO format without timezone indicator
      dateStr = timestamp + 'Z';
    }
    
    const date = new Date(dateStr);
    
    // Format with locale-specific options
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const calculateDuration = (job, isFailed = false) => {
    // Use retried_at if available, otherwise use created_at
    const startTime = job.retried_at || job.created_at;
    const endTime = isFailed ? job.updated_at : job.completed_at;
    
    if (!startTime || !endTime) return 'N/A';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMs = end - start;
    
    // Handle invalid dates or negative duration
    if (isNaN(durationMs) || durationMs < 0) return 'N/A';
    
    // Cap all durations at 15 minutes (900000 ms)
    // Lambda max execution time is 15 minutes
    // If it shows longer, it's a timestamp issue with old jobs
    const maxDuration = 900000; // 15 minutes
    const cappedDurationMs = Math.min(durationMs, maxDuration);
    
    // If duration was capped significantly (more than actual duration), show indicator
    const wasCapped = durationMs > maxDuration;
    
    const seconds = Math.floor(cappedDurationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    let timeStr;
    if (hours > 0) {
      timeStr = `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      timeStr = `${minutes}m ${seconds % 60}s`;
    } else {
      timeStr = `${seconds}s`;
    }
    
    // Add indicator if duration was capped
    return wasCapped ? `~${timeStr}` : timeStr;
  };

  const getModelDisplayName = (modelId) => {
    if (!modelId) return 'Default';
    
    // Extract readable model name from ID
    if (modelId.includes('claude-3-7-sonnet')) return 'Claude 3.7 Sonnet';
    if (modelId.includes('claude-3-5-sonnet')) return 'Claude 3.5 Sonnet';
    if (modelId.includes('claude-3-haiku')) return 'Claude 3 Haiku';
    if (modelId.includes('nova-pro')) return 'Nova Pro';
    if (modelId.includes('nova-lite')) return 'Nova Lite';
    if (modelId.includes('llama3-3-70b')) return 'Llama 3.3 70B';
    if (modelId.includes('llama3-2-90b')) return 'Llama 3.2 90B';
    if (modelId.includes('llama3-2-11b')) return 'Llama 3.2 11B';
    if (modelId.includes('llama3-2-3b')) return 'Llama 3.2 3B';
    if (modelId.includes('llama3-2-1b')) return 'Llama 3.2 1B';
    
    // Fallback: extract last part of ID
    const parts = modelId.split('.');
    const lastPart = parts[parts.length - 1] || modelId;
    return lastPart.split('-').slice(0, 3).join(' ').toUpperCase();
  };

  if (loading && !refreshing) {
    return (
      <div className="jobs-list-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-list-container">
      <div className="jobs-header">
        <h2>⚙️ Report Generation Jobs</h2>
        <button 
          className="btn-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          🔄 {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
          <button className="btn-retry" onClick={() => loadJobs()}>
            Retry
          </button>
        </div>
      )}

      {!error && jobs.length === 0 && (
        <div className="no-jobs">
          <p>📭 No jobs found</p>
          <p className="hint">Generate a report to see jobs here</p>
        </div>
      )}

      {!error && jobs.length > 0 && (
        <div className="jobs-table-container">
          <table className="jobs-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Idea/Prompt</th>
                <th>Backend & Model</th>
                <th>Duration</th>
                <th>Started</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr 
                  key={job.job_id} 
                  className={`job-row ${getStatusClass(job.status, job.error)}`}
                >
                  <td className="status-cell">
                    <span className="status-badge">
                      {getStatusIcon(job.status, job.error)} {getStatusText(job.status, job.error)}
                    </span>
                  </td>
                  <td className="idea-cell" title={job.request_payload?.prompt || job.request_payload?.idea || 'N/A'}>
                    {((job.request_payload?.prompt || job.request_payload?.idea || 'N/A').substring(0, 60))}
                    {((job.request_payload?.prompt || job.request_payload?.idea || '').length > 60) ? '...' : ''}
                  </td>
                  <td className="config-cell">
                    <div className="config-info">
                      <span className="backend-badge" title={job.request_payload?.backend || 'strands'}>
                        {job.request_payload?.backend === 'strands' ? '🤖 Multi-Agent' : '⚡ Traditional'}
                      </span>
                      <span className="model-badge" title={job.request_payload?.model_id || 'Default'}>
                        🧠 {getModelDisplayName(job.request_payload?.model_id)}
                      </span>
                    </div>
                  </td>
                  <td className="duration-cell">
                    <span className="duration-text">
                      {job.status === 'completed' 
                        ? calculateDuration(job, false)
                        : job.status === 'failed'
                        ? calculateDuration(job, true)
                        : '⏳ In progress'}
                    </span>
                  </td>
                  <td className="timestamp-cell">
                    <div style={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                      {formatTimestamp(job.retried_at || job.created_at)}
                      {job.retried_at && (
                        <span style={{fontSize: '10px', color: '#3498db', fontWeight: '600'}}>
                          ↻ Retried
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="action-cell">
                    <div className="action-buttons">
                      <button
                        className={`btn-view ${job.status === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleJobClick(job)}
                        disabled={job.status === 'processing' || job.status === 'queued'}
                      >
                        {job.status === 'completed' ? '📄 View Report' : 
                         job.status === 'failed' ? '❌ Details' : 
                         '⏳ In Progress'}
                      </button>
                      {job.status === 'failed' && (
                        <button
                          className="btn-retry"
                          onClick={(e) => handleRetryJob(job, e)}
                          title="Retry this job"
                        >
                          ↻
                        </button>
                      )}
                      <button
                        className="btn-copy-action"
                        onClick={(e) => copyJobId(job.job_id, e)}
                        title={`Copy Job ID: ${job.job_id}`}
                      >
                        📋
                      </button>
                      <button
                        className="btn-delete"
                        onClick={(e) => handleDeleteJob(job.job_id, e)}
                        title="Delete job"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="jobs-footer">
        <p className="auto-refresh-note">
          🔄 Auto-refreshing every 10 seconds
        </p>
        <p className="jobs-count">
          Total Jobs: {jobs.length} | 
          Completed: {jobs.filter(j => j.status === 'completed').length} | 
          Processing: {jobs.filter(j => j.status === 'processing' || j.status === 'queued').length} | 
          Failed: {jobs.filter(j => j.status === 'failed').length}
        </p>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.type === 'delete' ? 'Delete Job' : 'Retry Job'}
        message={
          confirmDialog.type === 'delete' 
            ? 'Are you sure you want to delete this job? This action cannot be undone.' 
            : 'Retry this failed job with the same parameters?'
        }
        type={confirmDialog.type === 'delete' ? 'danger' : 'warning'}
        confirmText={confirmDialog.type === 'delete' ? 'Delete' : 'Retry'}
        cancelText="Cancel"
        onConfirm={confirmDialog.type === 'delete' ? confirmDeleteJob : confirmRetryJob}
        onCancel={() => setConfirmDialog({ isOpen: false, type: '', job: null, jobId: null })}
      />
    </div>
  );
}

export default JobsList;
